import { useShopTimezone } from '@/contexts/TimezoneContext';
import { toShopTime, toUTC } from '@/utils/reminderHelpers';
import { format } from 'date-fns';
// DTC-G G-Optional-Low: route the `parseTimeInShopTZ` baseDate
// fallback through the canonical clock so the helper's default-base
// behavior is freezable under `clock.setFixed(...)`. Mechanical 1:1
// replacement; no semantic change at production wall-clock time.
import { clock } from '@/utils/clock';

/**
 * Hook for timezone-aware time utilities
 * Provides functions to convert between UTC and shop timezone
 */
export const useShopTime = () => {
  const { shopTimezone } = useShopTimezone();

  /**
   * Convert UTC date to shop timezone
   */
  const toShopTZ = (utcDate: Date): Date => {
    return toShopTime(utcDate, shopTimezone);
  };

  /**
   * Convert shop timezone date to UTC
   */
  const fromShopTZ = (shopDate: Date): Date => {
    return toUTC(shopDate, shopTimezone);
  };

  /**
   * Format a UTC date in shop timezone
   */
  const formatInShopTZ = (utcDate: Date, formatStr: string): string => {
    const shopDate = toShopTime(utcDate, shopTimezone);
    return format(shopDate, formatStr);
  };

  /**
   * Format time only (HH:mm or h:mm a)
   */
  const formatTimeInShopTZ = (utcDate: Date, use12Hour: boolean = true): string => {
    const shopDate = toShopTime(utcDate, shopTimezone);
    return format(shopDate, use12Hour ? 'h:mm a' : 'HH:mm');
  };

  /**
   * Parse a time string in shop timezone and convert to UTC
   * @param timeStr - Time in format "HH:mm" or "h:mm a"
   * @param baseDate - Date to use as base (defaults to today in shop TZ)
   */
  const parseTimeInShopTZ = (timeStr: string, baseDate?: Date): Date => {
    const base = baseDate ? toShopTime(baseDate, shopTimezone) : clock.now();
    
    // Parse time string
    const match = timeStr.match(/(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i);
    if (!match) {
      throw new Error(`Invalid time format: ${timeStr}`);
    }

    const [, hh, mm, ap] = match as unknown as [string, string, string, string?];
    let h = parseInt(hh, 10);
    const m = parseInt(mm, 10);
    
    if (ap) {
      const apu = ap.toUpperCase();
      if (apu === 'PM' && h < 12) h += 12;
      if (apu === 'AM' && h === 12) h = 0;
    }

    // Create date in shop timezone
    const shopDate = new Date(base);
    shopDate.setHours(h, m, 0, 0);

    // Convert to UTC
    return toUTC(shopDate, shopTimezone);
  };

  return {
    shopTimezone,
    toShopTZ,
    fromShopTZ,
    formatInShopTZ,
    formatTimeInShopTZ,
    parseTimeInShopTZ,
  };
};
